type FilterOption = {
  id: string;
  selected: boolean;
};

type Filter = {
  id: string;
  options: FilterOption[];
};

type CategoryGroup = {
  [key: string]: string[];
};

type FormattedMapping = {
  filters?: {
    [key: string]: {
      id: string;
      isActive: boolean;
    };
  };
  articleSource?: string;
  contentGroup?: string;
};

type GetParamsProps = {
  categories: CategoryGroup;
  formattedMapping: FormattedMapping;
  page: number;
  take: number;
  language: string;
};

type GenerateUrlProps = {
  approvedFilters: Array<{ group: string; id: string; preselected?: boolean }>;
  page: number;
  take: number;
  routePath: string;
};

export const useGetParams = ({
  categories,
  formattedMapping,
  page,
  take,
  language,
}: GetParamsProps): {
  search: {
    source?: string;
    contentGroup: string;
    language: string;
  };
  facets: string[];
  filters: {
    name: string;
    values: string[];
  }[];
  take: number;
  skip: number;
} => {
  let filters: string[] = [];
  if (formattedMapping.filters) {
    filters = Object.keys(formattedMapping.filters)
      .filter((item) => formattedMapping.filters![item].isActive)
      .map((item) => formattedMapping.filters![item].id);
  }
  const source: string | undefined = formattedMapping.articleSource;
  return {
    search: {
      source,
      contentGroup: formattedMapping.contentGroup || '',
      language: language ? language : 'en-GB',
    },
    facets: filters,
    filters: filters.map((filter) => {
      return {
        name: filter,
        values: categories && categories[filter]?.length ? categories[filter] : [],
      };
    }),
    take: take,
    skip: (page - 1) * take,
  };
};

export const prepareFilterCategories = (
  approvedFilters: Array<{ group: string; id: string }>
): CategoryGroup => {
  const categories: CategoryGroup = {};
  approvedFilters.forEach((option) => {
    if (categories[option.group]) {
      categories[option.group] = [...categories[option.group], option.id];
    } else {
      categories[option.group] = [option.id];
    }
  });
  return categories;
};

export const useGenerateUrl = ({
  approvedFilters,
  page,
  take,
  routePath,
}: GenerateUrlProps): void => {
  const approvedFiltersWithoutPreselected = approvedFilters.filter((filter) => !filter.preselected);
  const categories = prepareFilterCategories(approvedFiltersWithoutPreselected);

  let query: string = Object.keys(categories)
    .map((key, i) => {
      const categoryKeyValue = categories[key];
      if (categoryKeyValue) {
        return `${i === 0 ? '?' : '&'}${key}=${encodeURIComponent(categoryKeyValue.join(','))}`;
      } else {
        return '';
      }
    })
    .join('');

  if (page || take) {
    query = query ? query + `&page=${page}` : `?page=${page}`;
    if (page === 1) {
      query = query.replace('?page=1', '').replace('&page=1', '');
    }
    query = query ? query + `&take=${take}` : `?take=${take}`;
    if (take === 20) {
      query = query.replace('?take=20', '').replace('&take=20', '');
    }
  }
  history.pushState(null, '', `${query ? query : routePath}`);
};

export const formatFiltersForReq = (
  filters: { [key: string]: Filter },
  returnEmptyFilters: boolean
): {
  reqFacets: string[];
  reqFilters: Array<{
    name: string;
    values: string[];
  }>;
} => {
  const reqFacets: string[] = Object.values(filters).map((item) => item.id);
  const reqFilters = Object.values(filters).map((filter) => {
    return {
      name: filter.id,
      values: filter.options
        .filter((option) => option.selected && !returnEmptyFilters)
        .map((option) => option.id),
    };
  });
  return {
    reqFacets,
    reqFilters,
  };
};

export const useGetRequestParams = (
  filters: { [key: string]: Filter },
  language: string,
  { source, contentGroup }: { source?: string; contentGroup?: string },
  returnEmptyFilters: boolean = false
): {
  facets: string[];
  filters: Array<{ name: string; values: string[] }>;
  search: {
    source?: string;
    contentGroup?: string;
    language: string;
  };
} => {
  const { reqFacets, reqFilters } = formatFiltersForReq(filters, returnEmptyFilters);
  return {
    facets: reqFacets,
    filters: reqFilters,
    search: {
      source,
      contentGroup,
      language,
    },
  };
};
